import { useState } from 'react';
import { Button, Form, Input, Typography, Checkbox } from 'antd';
import { notify } from '../utils/notifications';
import { useConnection } from '../utils/connection';
import FloatingElement from '../components/layout/FloatingElement';
import styled from 'styled-components';
import { useWallet } from '@solana/wallet-adapter-react';
import { Keypair, SystemProgram, Transaction } from '@solana/web3.js';
import {
  createAssociatedTokenAccountInstruction,
  getMinimumBalanceForRentExemptMint,
  getAssociatedTokenAddress,
  MintLayout,
  createInitializeMintInstruction,
  createMintToInstruction,
} from '@solana/spl-token';
import { createFaucet } from '../utils/faucet';
import { TOKEN_PROGRAM_ID } from '../utils/tokens';
import { BN } from 'bn.js';

const { Text, Title } = Typography;

const Wrapper = styled.div`
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  margin-top: 24px;
  margin-bottom: 24px;
`;

export default function MintTokenPage() {
  const connection = useConnection();
  const { wallet, connected } = useWallet();

  const [submitting, setSubmitting] = useState(false);

  const [shouldCreateFaucet, setShouldCreateFaucet] = useState(false);
  const [maxAirdrop, setMaxAirdrop] = useState('1000');

  const [decimals, setDecimals] = useState('6');
  const [token, setToken] = useState('');
  const [faucet, setFaucet] = useState('');

  async function mintToken() {
    setSubmitting(true);

    const numDecimals = decimals as any as number;
    const walletPk = wallet!.adapter.publicKey!;
    const mintKeypair = Keypair.generate();
    const ataPk = await getAssociatedTokenAddress(
      mintKeypair.publicKey,
      walletPk,
      false,
    );
    const lamports = await getMinimumBalanceForRentExemptMint(connection);
    const latestBlockhash = await connection.getLatestBlockhashAndContext(
      'finalized',
    );

    const transaction = new Transaction({
      ...latestBlockhash.value,
      feePayer: walletPk,
    }).add(
      SystemProgram.createAccount({
        fromPubkey: walletPk,
        newAccountPubkey: mintKeypair.publicKey,
        space: MintLayout.span,
        lamports,
        programId: TOKEN_PROGRAM_ID,
      }),
      createInitializeMintInstruction(
        mintKeypair.publicKey,
        numDecimals,
        walletPk,
        walletPk,
        TOKEN_PROGRAM_ID,
      ),

      createAssociatedTokenAccountInstruction(
        walletPk,
        ataPk,
        walletPk,
        mintKeypair.publicKey,
      ),

      createMintToInstruction(
        mintKeypair.publicKey,
        walletPk,
        walletPk,
        1_000_000_000 * Math.pow(10, numDecimals),
      ),
    );
    transaction.partialSign(mintKeypair);
    const signature = await wallet!.adapter.sendTransaction(
      transaction,
      connection,
    );

    await connection.confirmTransaction({
      ...latestBlockhash.value,
      signature,
    });

    notify({
      message: 'Minted tokens',
      txid: signature,
      clusterUrl: connection.rpcEndpoint,
    });

    setToken(mintKeypair.publicKey.toString());

    if (shouldCreateFaucet) {
      const maxAirdropNum = maxAirdrop as any as number;
      if (maxAirdropNum < 1) {
        notify({ message: 'Invalid max airdrop amount', type: 'error' });
        return;
      }

      setFaucet(
        await createFaucet(
          connection,
          wallet!,
          mintKeypair.publicKey.toBase58(),
          new BN(Math.round(maxAirdropNum * Math.pow(10, numDecimals)), 10),
          walletPk.toBase58(),
        ),
      );
    }

    setSubmitting(false);
  }

  return (
    <Wrapper>
      <FloatingElement>
        <Title level={4}>Mint new token</Title>
        <Form onFinish={mintToken}>
          <Form.Item label="Decimals" initialValue={decimals}>
            <Input
              value={decimals}
              onChange={(e) => setDecimals(e.target.value.trim())}
              type="number"
              min="0"
              max="10"
              step="any"
            />
          </Form.Item>
          <Form.Item label="Create Faucet?" valuePropName="checked">
            <Checkbox
              checked={shouldCreateFaucet}
              onChange={() => setShouldCreateFaucet(!shouldCreateFaucet)}
            />
          </Form.Item>

          {!!shouldCreateFaucet ? (
            <Form.Item label="Max Airdrop Amount" initialValue={maxAirdrop}>
              <Input
                value={maxAirdrop}
                onChange={(e) => setMaxAirdrop(e.target.value.trim())}
                type="number"
                min="0"
                max="1000000"
                step="any"
              />
            </Form.Item>
          ) : undefined}

          <Form.Item label=" " colon={false}>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {connected ? 'Mint' : 'Not connected to wallet'}
            </Button>
          </Form.Item>
        </Form>
      </FloatingElement>

      {!!token ? (
        <FloatingElement>
          <Text>Mint address: {token}</Text>
          <br />
          {!!faucet ? <Text>Faucet address: {faucet}</Text> : <></>}
        </FloatingElement>
      ) : (
        <></>
      )}
    </Wrapper>
  );
}
// 2WGF5NgLZV3cg473ZNh41vzj3PU8HwLr3RB1PbyvCSXu
// 27YEf4ymtH8zxp7ym48XVRtCwyafcSHMuuZ4qXqB5ZNj
